import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	deleteTrainingCertification,
	getTrainingCertifications,
} from "../../../reducers/TrainingCertificationReducer";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	TableCell,
	TableRow,
} from "@mui/material";
import { ExpandMore, Redeem } from "@mui/icons-material";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import CustomTable from "../../../components/CustomTable";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import {
	formatDateToMonthDateYear,
	getDaysUntil,
} from "../../../utils/functions";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import AddEditCertificationDialog from "./AddEditCertificationDialog";

const tableHeaders = [
	"Certificate #",
	"Obtained",
	"Type",
	"Expiration",
	"Days Until",
	"Actions",
];

const CertificationsTable = (props) => {
	const { allExpanded, employeeNum, theme } = props;

	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const { trainingCertifications } = useSelector(
		(state) => state.trainingCertifications,
	);

	const [expanded, setExpanded] = useState(allExpanded);
	const [selectedId, setSelectedId] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [selectedCertification, setSelectedCertification] = useState(null);

	const certificatePermission = getAccessPermission(
		"employees",
		"Training Certifications",
		"any",
	);
	const canAddCertificate = certificatePermission?.Create ?? true;
	const canEditCertificate = certificatePermission?.Edit ?? true;
	const canDeleteCertificate = certificatePermission?.Delete ?? true;

	useEffect(() => {
		setExpanded(allExpanded);
	}, [allExpanded]);

	const getCertificationsData = () => {
		dispatch(
			getTrainingCertifications(
				0,
				-1,
				"id",
				"asc",
				{ employee: { value: employeeNum } },
				userAgency,
			),
		);
	};

	useEffect(() => {
		if (employeeNum && expanded && trainingCertifications?.length === 0) {
			getCertificationsData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [employeeNum, trainingCertifications, expanded, dispatch]);

	const handleDelete = useCallback(() => {
		if (selectedId) {
			dispatch(
				deleteTrainingCertification(
					selectedId,
					userData?.user?.ptsUserID,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
		}
	}, [dispatch, selectedId, userData]);

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded((prev) => !prev)}
				sx={{ mb: 1 }}
			>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					sx={{
						backgroundColor: theme.accordion.backgroundColor,
						color: theme.accordion.textColor,
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						width="100%"
					>
						<Box display="flex" alignItems="center">
							<Redeem sx={{ mr: 1 }} />
							<span>Training Certifications</span>
						</Box>
						<Box sx={{ mr: 2 }}>
							{canAddCertificate && (
								<PATAddButton
									tooltipText="Add Training Certification"
									onClick={(e) => {
										e.stopPropagation();
										setOpenAddDialog(true);
									}}
								/>
							)}
						</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					{trainingCertifications?.length > 0 ? (
						<CustomTable
							columns={tableHeaders}
							data={trainingCertifications}
							renderRow={(certification, index) => (
								<TableRow key={index}>
									<TableCell>
										{certification?.certificateNumber ||
											"-"}
									</TableCell>
									<TableCell>
										{certification?.dateObtained
											? formatDateToMonthDateYear(
													certification.dateObtained,
												)
											: "-"}
									</TableCell>
									<TableCell>
										{certification?.certificationType ||
											"-"}
									</TableCell>
									<TableCell>
										{certification?.expireDate
											? formatDateToMonthDateYear(
													certification.expireDate,
												)
											: "-"}
									</TableCell>
									<TableCell>
										{certification?.expireDate
											? getDaysUntil(
													certification?.expireDate,
												)
											: "-"}
									</TableCell>
									<TableCell>
										<Box display="flex" gap={1}>
											{canEditCertificate && (
												<PATEditButton
													tooltipText="Edit Certification"
													onClick={() => {
														setOpenAddDialog(true);
														setSelectedCertification(
															certification,
														);
													}}
													size="20px"
												/>
											)}
											{canDeleteCertificate && (
												<PATDeleteButton
													tooltipText="Delete Certification"
													onClick={() => {
														setSelectedId(
															certification?.id,
														);
														setOpenDeleteDialog(
															true,
														);
													}}
													size="20px"
												/>
											)}
										</Box>
									</TableCell>
								</TableRow>
							)}
						/>
					) : (
						<span>No training certifications found.</span>
					)}
				</AccordionDetails>
			</Accordion>

			<AddEditCertificationDialog
				open={openAddDialog}
				onClose={() => {
					setOpenAddDialog(false);
					setSelectedCertification(null);
				}}
				onSave={() => getCertificationsData()}
				employeeNum={employeeNum}
				initialData={selectedCertification}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Certification"
				message="Are you sure you want to delete this training certification?"
			/>
		</>
	);
};

export default CertificationsTable;
